import { Trans, useTranslation } from "react-i18next";
import { useCallback, useEffect, useRef, useState } from "react";
import type { ManualArElement, ManualBody } from "@arc/contracts";
import type { AuthSession } from "../../../auth/keycloak.js";
import { useManualBody } from "../useManualMutations.js";
import { StepArStrip, StepArElementBody, bindElementBody, FILE_SETTINGS } from "../manual-editor/StepArBlock.js";
import { ElementInfoProvider } from "../manual-editor/element-info.js";
import { StepMedia } from "../manual-editor/StepMedia.js";
import { locateWebglElement, type WebglElementLocation, type WebglPanelTarget } from "./webgl-panel.js";
import { useWebglBridge } from "./useWebglBridge.js";
import { useWebglWrites } from "./useWebglWrites.js";
import { signInFailureKey, useWebglSignIn, type WebglSignInState } from "./useWebglSignIn.js";
import type { TenantBootstrap } from "../../../tenant/bootstrap.js";
import { Button } from "../../../components/ui/button.js";
import { DraftScope } from "../manual-editor/draft-scope.js";
import { AssetLibraryDrawer } from "../asset-library/AssetLibraryDrawer.js";
import { SETTING_FILE_TYPES } from "../manual-editor/setting-file-types.js";

/**
 * The reduced single-element panel Unity embeds (ADR-044, item 1 of the WebGL path).
 *
 * **Unity is the HOST; this is the guest.** It renders as the whole document — no header, no footer, no navigation —
 * because it lives inside someone else's page. It is mounted from `main.tsx` *instead of* `<App/>`, never as a branch
 * inside it: `App` runs `useMe`, `usePresence`, `useMediaJobs` and `useFacilities` unconditionally, and a chrome-less
 * sub-panel must not open the presence socket and register the author as a second "online user" beside their own
 * Unity session (plan panel).
 *
 * **Lock-free**, per ADR-026 ("read mode / `webglViewMode` stay lock-free") and the legacy
 * (`DisplayManual(manual, false…)`, *"ohne Manual-Lock"*). The reasoning restated for an editing surface (ADR-044
 * D3): Unity already IS the editing session, so a second lease would let an author lock themselves out of their own
 * manual; collision safety comes from the per-write CAS version fence instead.
 *
 * **Phase 1 is READ-ONLY.** The legacy panel edits, so this is an increment toward parity, not parity — the write
 * handlers land in Phase 2. Every control below is therefore rendered disabled rather than omitted, so the panel's
 * shape is already the final one.
 */
export function WebglElementPanel({
  target,
  session: hostSession,
  signInTenant = null,
}: {
  target: WebglPanelTarget;
  session: AuthSession | null;
  /**
   * The tenant to sign in against, or **null when signing in here would be wrong** (ADR-050 D7).
   *
   * `main.tsx` passes a tenant only when this document is FRAMED: a top-level tab already runs the ordinary
   * redirect flow, so offering a popup there would be a second, worse way to do the same thing. Null disables the
   * affordance rather than hiding it, so there is no state where the button exists but cannot work.
   */
  signInTenant?: TenantBootstrap | null;
}) {
  const { t } = useTranslation();
  // Two sources, one session. The host supplies it on the top-level path; the popup supplies it inside the embed.
  const signIn = useWebglSignIn(signInTenant);
  const session = hostSession ?? signIn.session;
  const body = useManualBody(session, session ? target.manualId : null);
  // The legacy's `elementSelected=false` opens the step WITHOUT activating a tab (the warm-open preload).
  const [selectedId, setSelectedId] = useState<number | null>(target.elementSelected ? target.elementId : null);

  /** What Unity last heard about the selection — set by BOTH directions, so the dedup guard stays honest. */
  const lastSent = useRef<number | null>(null);

  // ── The Unity channel (ADR-049 Phase 1, RED ZONE) ──────────────────────────────────────────────────────────
  // Applying a remote selection goes through THIS setter, which no sender subscribes to — that is D5's echo
  // suppression: a call path rather than a flag, so there is no window in which a re-render could bounce the
  // selection back to Unity as if the user had made it.
  const applyRemoteSelection = useCallback((elementId: number, selectTab: boolean) => {
    setSelectedId(selectTab ? elementId : null);
    // `lastSent` means "what Unity last heard", and a selection Unity ITSELF made is something it has heard.
    // Leaving it stale made the dedup guard suppress a real event (diff panel): pick A, Unity moves to B, pick A
    // again — `A === lastSent` so nothing is sent, and the panel shows A while Unity stays on B.
    //
    // This records, it does not send, so D5's echo suppression is untouched: applying a remote selection still
    // reaches no sender.
    lastSent.current = elementId;
  }, []);

  const bridge = useWebglBridge({
    manualId: session ? target.manualId : null,
    realm: session?.realm,
    sub: session?.sub,
    body: body.data,
    selectElement: applyRemoteSelection,
  });

  // `webglReady` — ONCE, when the manual is first available. Unity holds a warm-open selection until it sees
  // this, so sending it twice would replay a selection the user has since moved away from.
  const announcedFor = useRef<number | null>(null);
  useEffect(() => {
    const id = body.data?.manual.id;
    if (id === undefined || announcedFor.current === id) return;
    announcedFor.current = id;
    bridge.send({ type: "webglReady", manualId: id });
  }, [body.data?.manual.id, bridge]);

  // The user picking a tab is the one outbound event Phase 1 has a trigger for. Deduped against the last value
  // sent, which is the legacy's `lastNotifiedSelectedId` guard against redraw-driven re-selects.
  const onUserSelect = useCallback(
    (elementId: number | null) => {
      setSelectedId(elementId);
      if (elementId === null || elementId === lastSent.current) return;
      lastSent.current = elementId;
      bridge.send({ type: "elementSelected", elementId });
    },
    [bridge],
  );

  // ── The states, in the order they can occur ──────────────────────────────────────────────────────────────────
  // No session. NOT a redirect: `main.tsx` deliberately skips the interactive Keycloak login on this route, because
  // navigating a third-party iframe into the auth flow either breaks on frame-ancestors or authenticates invisibly.
  //
  // Inside the embed there is now a way out of it — a popup on an explicit click (ADR-050). Outside one, there is
  // not, and the panel says so rather than offering a control that would do nothing.
  if (!session) {
    return signInTenant ? (
      <SignInPanel state={signIn.state} onSignIn={signIn.signIn} />
    ) : (
      <PanelMessage title={t("webgl.notSignedIn")}>{t("webgl.openFromEditor")}</PanelMessage>
    );
  }

  // ADR-044 D4 — we require `manualId`; there is no element→manual lookup to fall back to.
  if (target.manualId === null) {
    return (
      <PanelMessage title={t("webgl.missingManual")}>
        <Trans i18nKey="webgl.missingManualIdHint" components={{ code: <code>manualId</code> }} />
      </PanelMessage>
    );
  }

  if (body.isPending) return <PanelMessage title={t("webgl.loading")}>{t("webgl.loadingElement", { id: target.elementId })}</PanelMessage>;
  if (body.isError || !body.data) return <PanelMessage title={t("webgl.couldntLoad")}>{t("webgl.manualCouldntBeLoaded")}</PanelMessage>;

  const found = locateWebglElement(body.data, target.elementId);
  if (!found) {
    return (
      <PanelMessage title={t("webgl.elementNotFound")}>
        {t("webgl.elementGone", { id: target.elementId })}
      </PanelMessage>
    );
  }

  const { element, siblings } = found;
  const selected = siblings.find((e) => e.id === selectedId) ?? element;
  const step = found.kind === "step" ? found : null;

  return (
    <PanelBody
      body={body.data}
      bridge={bridge}
      element={element}
      selected={selected}
      siblings={siblings}
      step={step}
      selectedId={selectedId}
      onUserSelect={onUserSelect}
      session={session}
      manualId={target.manualId}
    />
  );
}

/**
 * The rendered panel, split out so the write hooks sit below every early return — a hook cannot be called after
 * a conditional `return`, and the states above (no session, no manual, loading, not found) all take one.
 */
function PanelBody({
  body,
  bridge,
  selected,
  siblings,
  step,
  selectedId,
  onUserSelect,
  session,
  manualId,
}: {
  body: ManualBody;
  bridge: ReturnType<typeof useWebglBridge>;
  element: ManualArElement;
  selected: ManualArElement;
  siblings: ManualArElement[];
  step: (WebglElementLocation & { kind: "step" }) | null;
  selectedId: number | null;
  onUserSelect: (id: number | null) => void;
  session: AuthSession | null;
  manualId: number | null;
}) {
  const { t } = useTranslation();

  /**
   * The picker target and the clipboard — both held HERE, which is above `StepArElementBody`'s `key` (D20).
   *
   * D14 keys that subtree by element id, so it REMOUNTS whenever the selection moves — and Unity drives those
   * moves over the bridge. A clipboard living inside it would be erased by the very navigation that makes it
   * useful: copy on element A, Unity selects element B, paste. `PanelBody` survives selection changes, so the
   * copied source outlives them.
   */
  const [actionsSlot, setActionsSlot] = useState<HTMLElement | null>(null);
  const [settingPicker, setSettingPicker] = useState<{ name: string; mode: "add" | "replace"; currentFileIds: number[]; elementId: number } | null>(null);
  const [copiedFrom, setCopiedFrom] = useState<{ elementId: number; label: string; settingNames: string[] } | null>(null);

  const writes = useWebglWrites({
    session,
    manualId,
    // ADR-044 D9 — the scope fork is READ from the resolved target, not chosen once: a step element and an
    // "Alle Schritte" element have different endpoints, and wiring only the step-scoped ones would render an
    // all-steps element perfectly and then fail every save on it.
    stepId: step ? step.stepId : null,
    body,
    bridge,
    fileSettingNames: FILE_SETTINGS,
  });

  return (
    <main className="bg-background text-foreground flex min-h-screen flex-col gap-4 p-4">
      {/* Enough context to know WHERE you are without the navigation that would let you leave — this panel is a
          fragment of Unity's screen, not a page of ours. The all-steps bucket says so instead of naming a step.
          A plain <div>, deliberately NOT a <header>: that would publish a `banner` LANDMARK into a document owned by
          Unity, where our fragment has no business claiming one. The <h1> still gives assistive tech its anchor. */}
      <div className="flex flex-col gap-0.5">
        <h1 className="m-0 text-sm font-medium">{step ? step.stepName : t("webgl.allSteps")}</h1>
        <p className="text-muted-foreground m-0 text-xs">{step ? step.sectionName : t("webgl.shownOnEveryStep")}</p>
      </div>

      {writes.error ? <p className="text-destructive text-xs">{t("manualEditor.settingSaveFailed")}</p> : null}

      <div className="flex flex-col gap-4 pt-4">
        {/* The RAW nullable state, not `selected.id` (diff panel). `selected` falls back to the target element so the
            body always has something to render; passing that fallback here re-activated the tab and made
            `elementSelected=false` — the warm-open preload — render identically to `=true`. */}
        <StepArStrip actionsSlotRef={setActionsSlot} elements={siblings} disabled selectedId={selectedId} onSelect={onUserSelect} busy={writes.busy} />
        {/* ABOVE the element key, which is the whole point (ADR-044 §11 D25). D14's key destroys the body on every
            Unity-driven selection change; this store outlives it, so a draft the author typed is restored when
            they come back — while still never appearing on the element they switched TO, because the slot is
            keyed by element AND setting. Nothing is written: flushing on a remote selection is forbidden by
            ADR-049 D2. */}
        <ElementInfoProvider session={session} manualId={body.manual.id} catalogueAvailable={body.manual.parentManualId === null}>
        <DraftScope id={selected.id}>
        <StepArElementBody
          // KEYED by element id, as `AllStepsCard` and `StepCard` both are (ADR-044 D14). `useScalarDraft`
          // retires a draft only when the SERVER value moves, so without this a draft typed on element A
          // survives a switch to B whenever both share a saved value — an empty string, overwhelmingly the
          // common case — and the next blur commits A's text onto B. Unity drives those switches over the
          // bridge, so it is a live path, not a corner.
          key={selected.id}
          actionsSlot={actionsSlot}
          element={selected}
          siblings={siblings}
          // ADR-044 §10 Phase 2b: the body is fully LIVE — file settings included. `isEditableHere` in
          // `StepArBlock` still treats a file setting without a picker as uneditable, which is what kept 2a
          // honest; now that a picker IS supplied the pill goes and the per-file Remove button is real.
          disabled={writes.onSetSetting === undefined}
          busySetting={writes.busy}
          copiedFrom={copiedFrom}
          {...bindElementBody({
            element: selected,
            siblings,
            // No `onDuplicate` — duplicating an element creates a SIBLING, and the panel shows one element
            // inside Unity's own selection model; the new element would exist with nothing selecting it.
            handlers: {
              onSetSetting: writes.onSetSetting,
              // 2b — the picker and the clipboard, live. Both are gated on the same `writes.*` being defined, so
              // a panel with no session still renders the read-only shape rather than dead controls.
              onPickSettingFile: writes.onPickedFile
                ? (elementId, name, mode, currentFileIds) => setSettingPicker({ elementId, name, mode, currentFileIds })
                : undefined,
              onCopySettings: writes.onPasteSettings ? (elementId, label, settingNames) => setCopiedFrom({ elementId, label, settingNames }) : undefined,
              onPasteSettings:
                writes.onPasteSettings && copiedFrom
                  ? (elementId) => writes.onPasteSettings?.(elementId, copiedFrom.elementId)
                  : undefined,
            },
            siblingsAfter: (b) => (step ? b.sections.flatMap((sec) => sec.steps).find((st) => st.id === step.stepId)?.elements : b.allStepsElements),
            onSelect: onUserSelect,
          })}
        >
          {/* ADR-044 D7. `StepArElementBody` strips Text/Image/Video for a MAIN element because on a main element
              those three ARE the step's content — the step card supplies them instead, and only when `isMain`.
              Composed bare, the common warm-open target would render with no instruction text and no media. Phase 1
              shows them read-only; the editable sections arrive with the write wiring in Phase 2. */}
          {selected.isMain && step ? <MainElementContent text={step.stepText} media={body.sections.flatMap((sec) => sec.steps).find((st) => st.id === step.stepId)?.media ?? null} session={session} manualId={body.manual.id} /> : null}
        </StepArElementBody>
        </DraftScope>
        </ElementInfoProvider>
        {/* The EDITOR'S drawer, unchanged (D15). D13 assumed it was "designed for the editor's viewport" and
            therefore a design question here; measured, it is a `fixed inset-0` overlay whose panel is
            `w-full max-w-3xl` — a MAX width of 768 px — and `fixed` is relative to THIS document, which is
            Unity's `PANEL_WIDTH` of 900 px. It fits, with backdrop either side, and full height is what the
            embed already is. There was no design decision to make, only an untested assumption. */}
        {settingPicker && writes.onPickedFile ? (
          <AssetLibraryDrawer
            session={session}
            manualId={manualId}
            isOpen
            mode="pick"
            // Every file-valued name filters to its own extensions — the server enforces the same list, so this
            // only keeps the author from being offered something Unity would fail to load, silently, in AR.
            types={SETTING_FILE_TYPES[settingPicker.name] ?? null}
            onClose={() => setSettingPicker(null)}
            onSelect={(file) => {
              const target = settingPicker;
              setSettingPicker(null);
              writes.onPickedFile?.(target.elementId, target.name, target.mode, target.currentFileIds, file.fileId);
            }}
          />
        ) : null}
      </div>
    </main>
  );
}

/** The main element's own content, read-only (Phase 1). The instruction text is server-sanitized rich text — the same
 *  string the reader renders — so it is injected the way the rest of the app injects sanitized manual HTML. */
function MainElementContent({
  text,
  media,
  session,
  manualId,
}: {
  text: string | null;
  media: React.ComponentProps<typeof StepMedia>["media"] | null;
  session: AuthSession | null;
  manualId: number;
}) {
  const { t } = useTranslation();
  if (!text && !media) return null;
  return (
    <div className="grid gap-4 md:grid-cols-[minmax(0,1fr)_15rem]">
      <div className="flex min-w-0 flex-col gap-1">
        <span className="text-muted-foreground text-xs font-medium">{t("webgl.instruction")}</span>
        {text ? (
          <div className="prose-arc text-sm" dangerouslySetInnerHTML={{ __html: text }} />
        ) : (
          <p className="text-muted-foreground m-0 text-sm">{t("webgl.noInstructionText")}</p>
        )}
      </div>
      {media ? (
        <div className="flex min-w-0 flex-col gap-2">
          <span className="text-muted-foreground text-xs font-medium">{t("webgl.media")}</span>
          <StepMedia media={media} session={session} manualId={manualId} thumbClassName="h-36 w-full" />
        </div>
      ) : null}
    </div>
  );
}

/** Every non-content state, in one shape. `role="status"` (not `alert`): these are all expected conditions of an
 *  embed — not signed in yet, still loading, a deleted element — and none of them warrants interrupting a screen
 *  reader mid-sentence in Unity's own page. */
/**
 * The panel's sign-in state (ADR-050 Phase 2).
 *
 * Built on `PanelMessage` so it inherits the same `bg-background text-foreground` ground every other panel state
 * uses — the panel paints its own surface because it is the whole document inside Unity's iframe, and a
 * transparent body would show the host page's colour through it in one theme only.
 *
 * The failure line is `role="alert"`, not `role="status"`: it appears in response to the user's own click and
 * replaces nothing, so it must be announced rather than queued politely.
 */
function SignInPanel({ state, onSignIn }: { state: WebglSignInState; onSignIn: () => void }) {
  const { t } = useTranslation();
  const busy = state.status === "busy";
  const failed = state.status === "failed";

  return (
    <main className="bg-background text-foreground flex min-h-screen flex-col items-start gap-3 p-4">
      <div className="flex flex-col items-start gap-1">
        <h1 className="m-0 text-sm font-medium">{t("webgl.notSignedIn")}</h1>
        <p className="text-muted-foreground m-0 text-sm">{t("webgl.signInToEdit")}</p>
      </div>

      {/* `aria-live` on a persistent node, not a mounted-on-demand one: a region that appears WITH its content is
          frequently missed by screen readers, which is why the wrapper is always rendered and only the text swaps. */}
      <p role="alert" className="text-destructive m-0 min-h-0 text-sm empty:hidden">
        {failed ? t(signInFailureKey(state.reason)) : null}
      </p>

      <Button size="sm" onPress={onSignIn} isDisabled={busy}>
        {busy ? t("webgl.signingIn") : failed ? t("webgl.tryAgain") : t("webgl.signIn")}
      </Button>
    </main>
  );
}

function PanelMessage({ title, children }: { title: string; children: React.ReactNode }) {
  return (
    <main className="bg-background text-foreground flex min-h-screen flex-col items-start gap-1 p-4">
      <h1 className="m-0 text-sm font-medium">{title}</h1>
      <p role="status" className="text-muted-foreground m-0 text-sm">
        {children}
      </p>
    </main>
  );
}
